import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api.js";
import { URLS, ImgURL } from "../utils/urls.js";
import { ArrowLeft, User, Mail, Calendar, CheckCircle, XCircle, CreditCard, Download, UserCheck } from "lucide-react";
import { toast } from "react-hot-toast";

const EventAttendees = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [attendees, setAttendees] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(URLS.GET_EVENT_ATTENDEES(id));
      setAttendees(res.data.attendees || []);
      setEvent(res.data.event);
    } catch (err) {
      toast.error("Failed to load attendees");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (attendeeId, currentStatus) => {
    try {
      await api.put(`/api/admin-events/attendees/${attendeeId}/check-in`, { checkedIn: !currentStatus });
      toast.success(currentStatus ? "Checked-out" : "Checked-in! ✨");
      fetchData();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const exportToCSV = () => {
    if (attendees.length === 0) return;
    
    const headers = ["First Name", "Last Name", "Email", "Status", "Payment", "Checked In", "Date Joined"];
    const rows = attendees.map(a => [
        a.firstName,
        a.lastName,
        a.email,
        a.status,
        a.paymentStatus,
        a.checkedIn ? "YES" : "NO",
        new Date(a.createdAt).toLocaleDateString()
    ]);

    let csvContent = "data:text/csv;charset=utf-8," 
        + headers.join(",") + "\n"
        + rows.map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `attendees_${event?.title || "event"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading attendees list...</div>;

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-center justify-between">
        <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition font-medium"
        >
            <ArrowLeft size={20} />
            Back to Events
        </button>
        <button
            onClick={exportToCSV}
            className="flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded-xl hover:bg-gray-900 transition shadow-sm text-sm"
        >
            <Download size={16} />
            Export CSV
        </button>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{event?.title}</h2>
          <div className="flex items-center gap-4 mt-2 text-gray-500">
            <div className="flex items-center gap-1">
              <Calendar size={16} className="text-[#db8654]" />
              <span>{new Date(event?.eventStartAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-1">
              <User size={16} className="text-[#db8654]" />
              <span>{attendees.length} Registered</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
            <div className="bg-indigo-50 px-4 py-2 rounded-xl border border-indigo-100">
                <span className="text-sm text-indigo-600 font-bold">Revenue: ${attendees.filter(a => a.paymentStatus === 'PAID').length * (event?.price || 0)}</span>
            </div>
            <span className="text-[10px] text-gray-400 font-medium">{attendees.filter(a => a.checkedIn).length} people checked in</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
            <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Check-in</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Payment</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Date Joined</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
                {attendees.map((attendee) => (
                <tr key={attendee.id} className={`hover:bg-gray-50/50 transition ${attendee.checkedIn ? 'bg-green-50/20' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                        <img 
                        src={ImgURL(attendee.profilePhotoUrl) || `https://ui-avatars.com/api/?name=${attendee.firstName}&background=random`} 
                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                        />
                        <span className="font-medium text-gray-900">{attendee.firstName} {attendee.lastName}</span>
                    </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                        <Mail size={12} />
                        <span>{attendee.email}</span>
                        </div>
                    </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                            onClick={() => handleCheckIn(attendee.id, attendee.checkedIn)}
                            className={`p-2 rounded-xl transition ${
                                attendee.checkedIn 
                                ? 'bg-green-600 text-white shadow-md' 
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }`}
                            title={attendee.checkedIn ? "Undo Check-in" : "Mark as Arrived"}
                        >
                            <UserCheck size={18} />
                        </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${
                        attendee.status === 'JOINED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                        {attendee.status}
                    </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                        {attendee.paymentStatus === 'PAID' ? (
                        <div className="flex items-center gap-1 text-green-600 font-bold text-sm">
                            <CheckCircle size={14} />
                            <span>Paid</span>
                        </div>
                        ) : (
                        <div className="flex items-center gap-1 text-orange-500 font-bold text-sm">
                            <CreditCard size={14} />
                            <span>{event?.price > 0 ? 'Pending' : 'Free'}</span>
                        </div>
                        )}
                    </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(attendee.createdAt).toLocaleDateString()}
                    </td>
                </tr>
                ))}
                {attendees.length === 0 && (
                <tr>
                    <td colSpan="6" className="px-6 py-10 text-center text-gray-400">
                    No attendees registered for this event yet.
                    </td>
                </tr>
                )}
            </tbody>
            </table>
        </div>
      </div>
    </div>
  );
};

export default EventAttendees;
